import {
  NotificationType as StoreNotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import { memo, useEffect, useMemo, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import "@/components/workspace/workspace.css";
import {
  Notification,
  NotificationType as EtUiNotificationType,
} from "@ever-tutor/et-ui";
import TutorViewNotification, {
  TUTOR_VIEW_NOTIFICATION_SUPPORTED_TYPES,
  NOTIFICATION_SUPPORTED_TYPES,
} from "./TutorViewNotification";
import DynamicTutorViewNotification from "./DynamicTutorViewNotification";
import ActionNotification from "./ActionNotification";
import ChannelWarningNotification from "./ChannelWarningNotification";
import type { Notification as NotificationType } from "@/store/useNotificationStore";
import type { NotificationStack as NotificationStackType } from "@/constants/notification";

const NotificationStack = ({ stack }: { stack: NotificationStackType }) => {
  const allNotifications = useNotificationStore((s) => s.notifications);
  const removeNotification = useNotificationStore((s) => s.removeNotification);
  const notifications = useMemo(
    () => allNotifications.filter((n) => n.stack.id === stack.id),
    [allNotifications, stack.id],
  );
  const stackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (stackRef.current) {
      stackRef.current.scrollTop = stackRef.current.scrollHeight;
    }
  }, [notifications]);

  const getNotificationComponent = (notification: NotificationType) => {
    // Checked before the type table: an action has to reach the screen whatever
    // type carries it, and the shared `Notification` has nowhere to put it.
    if (notification.action) {
      return (
        <ActionNotification
          key={notification.id}
          message={notification.message}
          action={notification.action}
          onClose={() => removeNotification(notification.id)}
        />
      );
    }

    if (notification.type === StoreNotificationType.CHANNEL_WARNING) {
      return (
        <ChannelWarningNotification
          key={notification.id}
          message={notification.message}
          onClose={() => removeNotification(notification.id)}
        />
      );
    }

    if (TUTOR_VIEW_NOTIFICATION_SUPPORTED_TYPES.includes(notification.type)) {
      if (notification.titleTemplate) {
        return (
          <DynamicTutorViewNotification
            key={notification.id}
            id={notification.id}
            type={notification.type}
            titleTemplate={notification.titleTemplate}
            titleData={notification.titleData}
            durationMs={notification.durationMs}
            subheading={notification.subheading || ""}
            onClose={() => removeNotification(notification.id)}
          />
        );
      }
      return (
        <TutorViewNotification
          key={notification.id}
          id={notification.id}
          type={notification.type}
          title={notification.title || notification.message}
          subheading={notification.subheading || ""}
          onClose={() => removeNotification(notification.id)}
        />
      );
    }

    if (NOTIFICATION_SUPPORTED_TYPES.includes(notification.type)) {
      return (
        // The wrapper restyles et-ui's lime pill in the workspace palette
        // (workspace.css); the package takes no className of its own.
        <div
          key={notification.id}
          className="et-toast"
          data-type={notification.type}
        >
          <Notification
            id={notification.id}
            message={notification.message}
            type={notification.type as string as EtUiNotificationType}
            count={notification.count}
            aria-label={notification.message}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div
      id={`notification-stack-${stack.id}`}
      ref={stackRef}
      className={`${stack.position} overflow-y-auto m-4 p-4`}
    >
      <AnimatePresence>
        {notifications.map((notification: NotificationType) =>
          getNotificationComponent(notification),
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(NotificationStack);
